import { DrawOpts, Item } from '../../../../types';
import { shapeNames } from '../../../constants';
import { getNoteBodyAttrs } from './text';

interface StickyPathDrawOpts {
  width: number;
  height: number;
  cornerSize: number;
}

const getStickyPath = ({ width, height, cornerSize }: StickyPathDrawOpts) => {
  return [
    `M 0 0`,
    `L ${width} 0`,
    `L ${width} ${height - cornerSize}`,
    `L ${width - cornerSize} ${height}`,
    `L 0 ${height}`,
    `Z`,
  ].join('');
};

const getCornerPath = ({ width, height, cornerSize }: StickyPathDrawOpts) => {
  return [
    ['M', width, height - cornerSize],
    ['L', width - cornerSize, height - cornerSize],
    ['L', width - cornerSize, height],
    ['Z'],
  ];
};

export const drawSticky = ({ group, options, size }: DrawOpts) => {
  const [width, height] = size;
  const { width: cornerSize } = options[shapeNames.corner];
  const path = getStickyPath({ width, height, cornerSize });

  // Shadow area.  Needed otherwise the shadow looks like it is in the note.
  group.addShape('path', {
    attrs: { path, ...options[shapeNames.shadow] },
    name: shapeNames.shadow,
    className: shapeNames.shadow,
  });

  // Sticky shape
  const keyShape = group.addShape('path', {
    attrs: { path, ...options[shapeNames.wrapper] },
    name: shapeNames.wrapper,
    className: shapeNames.wrapper,
  });

  // Flipped corner of sticky shape
  group.addShape('path', {
    attrs: { path: getCornerPath({ width, height, cornerSize }), ...options[shapeNames.corner] },
    name: shapeNames.corner,
    className: shapeNames.corner,
    zIndex: 5,
  });

  return keyShape;
};

export const updateSticky = ({ group, options, item, size }: DrawOpts & { item: Item }) => {
  const keyShape = item.getKeyShape();
  const shadow = group.findByClassName(shapeNames.shadow);
  const corner = group.findByClassName(shapeNames.corner);

  const [width, height] = size;
  const { width: cornerSize } = options[shapeNames.corner];
  const path = getStickyPath({ width, height, cornerSize });

  shadow.attr({ path });
  keyShape.attr({ ...options.style, ...options[shapeNames.wrapper], path });
  corner.attr({ path: getCornerPath({ width, height, cornerSize }) });
};

/**
 * Gets the size of the note when the entire body is visible
 */
export const getExpandedNoteSize = (
  opts: Pick<DrawOpts, 'options' | 'size' | 'model'>,
): [number, number] => {
  const [width, height] = opts.size;
  const { height: truncatedHeight } = getNoteBodyAttrs({ ...opts, isTruncated: true });
  const { height: expandedHeight } = getNoteBodyAttrs({ ...opts, isTruncated: false });
  const heightDiff = expandedHeight - truncatedHeight;

  return [width, height + Math.max(0, heightDiff)];
};
